import type {
  ContainerDimensions,
  Coordinates,
  Dimensions,
  PanBounds
} from "../model/types/ArtworkPreviewTypes";
import {
  DESKTOP_CONTAINER_SIZE,
  MOBILE_CONTAINER_PADDING,
  MOBILE_MAX_HEIGHT_RATIO,
  ZOOM_CONFIG
} from "./constants";

export const constrainToBounds = (coordinates: Coordinates, bounds: PanBounds): Coordinates => ({
  x: Math.max(bounds.minX, Math.min(bounds.maxX, coordinates.x)),
  y: Math.max(bounds.minY, Math.min(bounds.maxY, coordinates.y))
});

export const calculatePanBounds = (
  currentZoom: number,
  options: {
    isDesktop: boolean;
    containerRef: React.RefObject<HTMLDivElement | null>;
    imageLoaded: boolean;
  }
): PanBounds => {
  const { isDesktop, containerRef, imageLoaded } = options;

  if (!containerRef.current || !imageLoaded || currentZoom <= ZOOM_CONFIG.min) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }

  const containerWidth = isDesktop ? DESKTOP_CONTAINER_SIZE : containerRef.current.offsetWidth;
  const containerHeight = isDesktop ? DESKTOP_CONTAINER_SIZE : containerRef.current.offsetHeight;

  const scaledImageWidth = containerWidth * currentZoom;
  const scaledImageHeight = containerHeight * currentZoom;

  const excessWidth = Math.max(0, scaledImageWidth - containerWidth);
  const excessHeight = Math.max(0, scaledImageHeight - containerHeight);

  const maxPanX = excessWidth / 2;
  const maxPanY = excessHeight / 2;

  return {
    minX: -maxPanX,
    maxX: maxPanX,
    minY: -maxPanY,
    maxY: maxPanY
  };
};

export const calculateContainerDimensions = (
  isDesktop: boolean,
  imageLoaded: boolean,
  imageDimensions: Dimensions
): ContainerDimensions => {
  if (isDesktop) {
    return {
      width: `${DESKTOP_CONTAINER_SIZE}px`,
      height: `${DESKTOP_CONTAINER_SIZE}px`
    };
  }

  if (!imageLoaded || !imageDimensions.width || !imageDimensions.height) {
    return { width: "100%", height: "400px" };
  }

  const aspectRatio = imageDimensions.width / imageDimensions.height;
  const maxWidth = window.innerWidth - MOBILE_CONTAINER_PADDING;
  const maxHeight = window.innerHeight * MOBILE_MAX_HEIGHT_RATIO;

  let containerWidth = maxWidth;
  let containerHeight = containerWidth / aspectRatio;

  if (containerHeight > maxHeight) {
    containerHeight = maxHeight;
    containerWidth = containerHeight * aspectRatio;
  }

  return {
    width: `${Math.round(containerWidth)}px`,
    height: `${Math.round(containerHeight)}px`
  };
};
